import { useCallback, useEffect, useMemo, useState } from "react";

import { useConfirmationPage } from "./use-confirmation-page";
import { ConfirmationPageType } from "./use-confirmation-page-type";

import { useSearchParameters } from ".";

/**
 * useUserSegJiraTemplateRedesign
 *
 * Hook that determines to enroll a user into ATLAS_58859
 * @returns object
 */
export function useUserSegJiraTemplateRedesign(): {
    userSegmentationEnrollmentPreconditionsMet: boolean;
    templateChoiceEnrollmentPreconditionsMet: boolean;
    getUserSegmentationPageType: (
        oldOption: ConfirmationPageType,
        redesignOption: ConfirmationPageType
    ) => ConfirmationPageType;
    getTemplateChoicePageType: (
        oldOption: ConfirmationPageType,
        redesignOption: ConfirmationPageType
    ) => ConfirmationPageType;
} {
    const { products, n2n, templateId } = useSearchParameters();
    const { availableProducts } = useConfirmationPage();
    const [canN2EView, setCanN2EView] = useState(false);

    useEffect(() => {
        try {
            const isProvisioningOnlyJSW =
                products.length === 1 &&
                products[0] === "jira-software.ondemand";

            if (
                n2n === false &&
                isProvisioningOnlyJSW &&
                products.length === 1
            ) {
                const jiraSites =
                    availableProducts?.sites?.filter((site) =>
                        site.products.includes("jira-software.ondemand")
                    ) ?? [];

                // N2E users should see user seg if they do not have Jira
                setCanN2EView(jiraSites.length === 0);
            }
        } catch (error) {
            console.log(error);
        }
    }, [n2n, products, availableProducts]);

    const userSegmentationEnrollmentPreconditionsMet = useMemo(() => {
        const hasTemplateRecommendation = Boolean(templateId);
        const isProvisioningOnlyJSW =
            products.length === 1 && products[0] === "jira-software.ondemand";
        return (
            !hasTemplateRecommendation &&
            isProvisioningOnlyJSW &&
            (n2n || canN2EView)
        );
    }, [templateId, products, n2n, canN2EView]);

    const getUserSegmentationPageType = useCallback(
        (
            oldOption: ConfirmationPageType,
            redesignOption: ConfirmationPageType
        ) => {
            if (!userSegmentationEnrollmentPreconditionsMet) {
                return oldOption;
            }

            return redesignOption;
        },
        [userSegmentationEnrollmentPreconditionsMet]
    );

    const templateChoiceEnrollmentPreconditionsMet = useMemo(() => {
        const hasTemplateRecommendation = Boolean(templateId);
        const isProvisioningOnlyJSW =
            products.length === 1 && products[0] === "jira-software.ondemand";
        return !hasTemplateRecommendation && isProvisioningOnlyJSW;
    }, [templateId, products]);

    const getTemplateChoicePageType = useCallback(
        (
            oldOption: ConfirmationPageType,
            redesignOption: ConfirmationPageType
        ) => {
            if (!templateChoiceEnrollmentPreconditionsMet) {
                return oldOption;
            }

            if (n2n) {
                return redesignOption;
            }

            return redesignOption;
        },
        [templateChoiceEnrollmentPreconditionsMet, n2n]
    );

    return {
        userSegmentationEnrollmentPreconditionsMet,
        templateChoiceEnrollmentPreconditionsMet,
        getUserSegmentationPageType,
        getTemplateChoicePageType
    };
}
